/**
 * Cart Pricing Utilities
 * 
 * Helper functions untuk calculate cart totals dengan customer pricing tier.
 */

import { Customer, PricingTier } from '../services/customerService';
import { calculatePricing } from './pricingTier';

export interface CartItem {
  item: {
    id: number;
    name: string;
    sku: string;
    sell_price: number;
    [key: string]: any;
  };
  qty: number;
  price: number;
  discount: number;
}

export interface CartPricingSummary {
  subtotal: number;
  totalDiscount: number;
  total: number;
  items: Array<{
    itemId: number;
    quantity: number;
    basePrice: number;
    unitPrice: number;
    subtotal: number;
    discountAmount: number;
    total: number;
  }>;
}

/**
 * Calculate cart totals with customer pricing tier.
 */
export const calculateCartWithPricing = (
  cartItems: CartItem[],
  customer: Customer | null,
  paymentMethod: 'cash' | 'tempo' = 'cash',
  paymentDueDays: number = 0
): CartPricingSummary => {
  let grandSubtotal = 0;
  let grandDiscount = 0;
  let grandTotal = 0;
  const itemDetails: CartPricingSummary['items'] = [];

  for (const cartItem of cartItems) {
    const basePrice = cartItem.item.sell_price;
    const quantity = cartItem.qty;

    if (customer) {
      // Calculate with pricing tier
      const pricing = calculatePricing(customer.pricing_tier, {
        basePrice,
        quantity,
        paymentMethod,
        paymentDueDays,
      });

      grandSubtotal += pricing.subtotal;
      grandDiscount += pricing.discountAmount;
      grandTotal += pricing.total;

      itemDetails.push({
        itemId: cartItem.item.id,
        quantity,
        basePrice,
        unitPrice: pricing.unitPrice,
        subtotal: pricing.subtotal,
        discountAmount: pricing.discountAmount,
        total: pricing.total,
      });
    } else {
      // No customer = no discount
      const subtotal = basePrice * quantity;
      grandSubtotal += subtotal;
      grandTotal += subtotal;

      itemDetails.push({
        itemId: cartItem.item.id,
        quantity,
        basePrice,
        unitPrice: basePrice,
        subtotal,
        discountAmount: 0,
        total: subtotal,
      });
    }
  }

  return {
    subtotal: grandSubtotal,
    totalDiscount: grandDiscount,
    total: grandTotal,
    items: itemDetails,
  };
};

/**
 * Apply pricing tier to individual cart item.
 */
export const applyPricingToItem = (
  cartItem: CartItem,
  customer: Customer | null,
  paymentMethod: 'cash' | 'tempo' = 'cash',
  paymentDueDays: number = 0
): CartItem => {
  if (!customer) {
    // No pricing tier
    return {
      ...cartItem,
      price: cartItem.item.sell_price,
      discount: 0,
    };
  }

  const pricing = calculatePricing(customer.pricing_tier, {
    basePrice: cartItem.item.sell_price,
    quantity: cartItem.qty,
    paymentMethod,
    paymentDueDays,
  });

  return {
    ...cartItem,
    price: pricing.unitPrice,
    discount: pricing.discountAmount / cartItem.qty, // Discount per unit
  };
};

/**
 * Recalculate all cart items with pricing tier.
 */
export const recalculateCartItems = (
  cartItems: CartItem[],
  customer: Customer | null,
  paymentMethod: 'cash' | 'tempo' = 'cash',
  paymentDueDays: number = 0
): CartItem[] => {
  return cartItems.map(item => 
    applyPricingToItem(item, customer, paymentMethod, paymentDueDays)
  );
};

/**
 * Validate gold tier tempo rules.
 */
export const validateGoldTempo = (
  customer: Customer | null,
  paymentMethod: string,
  paymentDueDays: number
): { valid: boolean; message?: string } => {
  if (!customer) {
    return { valid: true };
  }

  if (
    customer.pricing_tier === PricingTier.GOLD &&
    paymentMethod === 'tempo' &&
    paymentDueDays > 7
  ) {
    return {
      valid: false,
      message: 'Gold tier discount hanya berlaku untuk tempo maksimal 7 hari. Tempo >7 hari tidak dapat diskon.',
    };
  }

  return { valid: true };
};

/**
 * Get pricing summary text for display.
 */
export const getPricingSummaryText = (
  customer: Customer | null,
  summary: CartPricingSummary
): string => {
  if (!customer) {
    return `Total: Rp ${summary.total.toLocaleString('id-ID')}`;
  }

  if (summary.totalDiscount === 0) {
    return `Total: Rp ${summary.total.toLocaleString('id-ID')} (No discount)`;
  }

  if (summary.totalDiscount > 0) {
    // Markup case
    return `Total: Rp ${summary.total.toLocaleString('id-ID')} (Markup +Rp ${summary.totalDiscount.toLocaleString('id-ID')})`;
  } else {
    // Discount case
    return `Total: Rp ${summary.total.toLocaleString('id-ID')} (Diskon Rp ${Math.abs(summary.totalDiscount).toLocaleString('id-ID')})`;
  }
};
